import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import { EventsCities } from "../../Data/EventsData";
import CreatableSelect from "react-select/creatable"; // Switched to Creatable
import { submitEventixInquiry } from "../../Backend/website";
import { EventsTypes } from "../../Data/EventsData";

const planEventSchema = yup.object().shape({
    eventType: yup.string().required("Please select an event type"),
    date: yup.string().required("Please select a date"),
    guests: yup.number().min(10, "Min 10 guests").required("Required"),
    city: yup.string().required("Please select a location"),
    budget: yup.string().required("Please select a budget"),
    message: yup.string(),
    fullName: yup.string().min(3, "Name too short").required("Name is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    phone: yup.string().matches(/^[6-9]\d{9}$/, "Invalid phone").required("Phone is required"),
});

const PlanEventModal = ({ isOpen, onClose }) => {
    const [step, setStep] = useState(1);
    const [loader, setLoader] = useState(false);

    const { register, handleSubmit, control, watch, setValue, trigger, formState: { errors }, reset } = useForm({
        resolver: yupResolver(planEventSchema),
        defaultValues: {
            eventType: "",
            guests: 150,
            city: "",
            date: "",
            budget: "",
            fullName: "",
            email: "",
            phone: ""
        }
    });

    const currentEventType = watch("eventType");
    const currentGuests = watch("guests");

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            reset();
        }
    }, [isOpen, reset]);

    const handleNext = async () => {
        let isValid = false;
        if (step === 1) {
            isValid = await trigger(["eventType", "date", "guests", "city"]);
        } else if (step === 2) {
            isValid = await trigger(["budget", "message"]);
        }
        if (isValid) setStep((prev) => prev + 1);
    };

    const handleBack = () => setStep((prev) => prev - 1);

    const onSubmit = async (data) => {
        setLoader(true);
        const result = await submitEventixInquiry(data);
        setLoader(false);
        if (result.success) {
            onClose();
            Swal.fire({
                icon: "success",
                title: "Thank You!",
                text: "We have received your enquiry and will connect with you shortly.",
                confirmButtonColor: "#0B1F33",
                timer: 3000,
                timerProgressBar: true,
                showConfirmButton: false,
            });
        } else {
            Swal.fire({
                icon: "error",
                title: "Submission Failed",
                text: "Something went wrong. Please try again.",
                confirmButtonColor: "#d33",
            });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 font-sans">
            <div className="absolute inset-0 bg-[#0B1F33]/70 backdrop-blur-sm" onClick={onClose}></div>

            {/* Modal Container: Reduced max-height and more compact padding */}
            <div className="relative w-full max-w-[480px] bg-[#F8F9FA] rounded-[2rem] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 z-[70] w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-100 text-[#0B1F33] hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <span className="material-symbols-outlined text-xl">close</span>
                </button>

                {/* Progress Header: More compact */}
                <div className="px-8 pt-6 pb-2 shrink-0">
                    <div className="flex justify-between items-center mb-3">
                        <span className="text-[10px] font-bold text-[#0B1F33] uppercase tracking-widest">
                            Step {step} of 3
                        </span>
                    </div>
                    <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-[#0B1F33] transition-all duration-500 ease-out"
                            style={{ width: `${(step / 3) * 100}%` }}
                        ></div>
                    </div>
                </div>

                {/* Scrollable Content Area */}
                <div className="overflow-y-auto px-8 pb-8 pt-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="text-center mb-6">
                            <h2 className="text-xl font-black text-[#0B1F33] mb-1">
                                {step === 1 ? "Plan Your Perfect Event" : step === 2 ? "Budget & Details" : "Contact Info"}
                            </h2>
                            <p className="text-xs text-gray-500 font-medium">
                                {step === 1 ? "Tell us a little about what you're planning." :
                                    step === 2 ? "Help us customize the experience." :
                                        "Where should we send your proposal?"}
                            </p>
                        </div>

                        {step === 1 && (
                            <div className="space-y-4">
                                {/* Event Type Grid */}
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-2">Event Type</label>
                                    <div className="grid grid-cols-2 gap-2.5">
                                        {Object.values(EventsTypes).map((category) => {
                                            const type = category.info;
                                            return (
                                                <div
                                                    key={type.id}
                                                    onClick={() => setValue("eventType", type.id, { shouldValidate: true })}
                                                    className={`
                                                        relative cursor-pointer p-3 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all duration-300
                                                        ${currentEventType === type.id
                                                            ? "border-[#0B1F33] bg-[#0B1F33]/5 scale-[1.02]"
                                                            : "border-gray-100 bg-white hover:border-gray-200"}
                                                    `}
                                                >
                                                    {currentEventType === type.id && (
                                                        <div className="absolute top-1.5 right-1.5 bg-[#0B1F33] rounded-full p-0.5">
                                                            <span className="material-symbols-outlined text-white text-[10px] font-bold">check</span>
                                                        </div>
                                                    )}
                                                    <span className={`material-symbols-outlined text-2xl ${currentEventType === type.id ? "text-[#0B1F33]" : "text-gray-400"}`}>
                                                        {type.icon}
                                                    </span>
                                                    <span className={`text-[10px] uppercase tracking-wider font-black ${currentEventType === type.id ? "text-[#0B1F33]" : "text-gray-500"}`}>
                                                        {type.label}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {errors.eventType && <p className="text-red-500 text-[10px] mt-1">{errors.eventType.message}</p>}
                                </div>

                                {/* Date Input - Forced calendar open on click */}
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">Event Date</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="material-symbols-outlined text-gray-400 text-lg">calendar_month</span>
                                        </div>
                                        <input
                                            type="date"
                                            {...register("date")}
                                            onClick={(e) => e.target.showPicker?.()}
                                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-[#0B1F33] focus:border-[#D4AF37] outline-none cursor-pointer"
                                        />
                                    </div>
                                    {errors.date && <p className="text-red-500 text-[10px] mt-1">{errors.date.message}</p>}
                                </div>

                                {/* Guest Slider */}
                                <div>
                                    <div className="flex justify-between items-center mb-1.5">
                                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Approx. Guests</label>
                                        <span className="text-xs font-black text-[#0B1F33]">{currentGuests}+</span>
                                    </div>
                                    <input
                                        type="range" min="10" max="1000" step="10"
                                        {...register("guests")}
                                        className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#0B1F33]"
                                    />
                                </div>

                                {/* Location Select - Creatable */}
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">Preferred Location</label>
                                    <div className="relative">
                                        <Controller
                                            control={control}
                                            name="city"
                                            render={({ field: { onChange, value, ref } }) => (
                                                <CreatableSelect
                                                    inputRef={ref}
                                                    options={EventsCities?.map((item) => ({ value: item.city, label: item.city }))}
                                                    value={value ? { value, label: value } : null}
                                                    onChange={(val) => onChange(val ? val.value : "")}
                                                    placeholder="Search or type city..."
                                                    isSearchable={true}
                                                    unstyled
                                                    classNames={{
                                                        control: () => "w-full px-4 py-1 bg-white border border-gray-200 rounded-xl text-sm font-bold text-[#0B1F33] focus-within:border-[#D4AF37] cursor-text",
                                                        menu: () => "bg-white border border-gray-100 rounded-xl mt-2 shadow-xl z-50",
                                                        option: ({ isFocused }) => `px-4 py-2 text-sm ${isFocused ? "bg-[#D4AF37] text-white" : "text-[#0B1F33]"}`,
                                                        placeholder: () => "text-gray-400 font-normal",
                                                    }}
                                                    components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                                                />
                                            )}
                                        />
                                    </div>
                                    {errors.city && <p className="text-red-500 text-[10px] mt-1">{errors.city.message}</p>}
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="space-y-5">
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-3">Estimated Budget</label>
                                    <div className="grid grid-cols-1 gap-2">
                                        {["< 5 Lakh", "5 - 20 Lakh", "20 Lakh - 1 Cr", "1 Cr +"].map((b) => (
                                            <label key={b} className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${watch("budget") === b ? "border-[#0B1F33] bg-white shadow-sm" : "border-gray-200 bg-white"}`}>
                                                <input type="radio" value={b} {...register("budget")} className="accent-[#0B1F33] w-4 h-4" />
                                                <span className="text-sm font-bold text-[#0B1F33]">{b}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {errors.budget && <p className="text-red-500 text-[10px] mt-1">{errors.budget.message}</p>}
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-2">Special Requirements</label>
                                    <textarea {...register("message")} rows="3" placeholder="Tell us about your theme..." className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-[#0B1F33] outline-none resize-none"></textarea>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="space-y-4">
                                <div className="grid gap-3">
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">Full Name</label>
                                        <input type="text" {...register("fullName")} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-[#0B1F33] outline-none" placeholder="Rahul Sharma" />
                                        {errors.fullName && <p className="text-red-500 text-[10px] mt-1">{errors.fullName.message}</p>}
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">Email Address</label>
                                        <input type="email" {...register("email")} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-[#0B1F33] outline-none" placeholder="rahul@example.com" />
                                        {errors.email && <p className="text-red-500 text-[10px] mt-1">{errors.email.message}</p>}
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">Phone Number</label>
                                        <input type="tel" {...register("phone")} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-[#0B1F33] outline-none" placeholder="+91 98765 43210" />
                                        {errors.phone && <p className="text-red-500 text-[10px] mt-1">{errors.phone.message}</p>}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-8 flex gap-3">
                            {step > 1 && <button type="button" onClick={handleBack} className="flex-1 py-3 rounded-xl border border-gray-200 text-[#0B1F33] font-bold text-sm">Back</button>}
                            {step < 3 ? (
                                <button type="button" onClick={handleNext} className="flex-[2] py-3 rounded-xl bg-[#D4AF37] text-[#0B1F33] font-black text-xs uppercase flex items-center justify-center gap-2">
                                    Next Step <span className="material-symbols-outlined text-lg">arrow_forward</span>
                                </button>
                            ) : (
                                <button type="submit" disabled={loader} className="flex-[2] py-3 rounded-xl bg-[#0B1F33] text-white font-black text-xs uppercase flex items-center justify-center gap-2">
                                    {loader ? "Sending..." : "Submit Request"} {!loader && <span className="material-symbols-outlined text-lg">send</span>}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PlanEventModal;